export default String.raw`
const CouponPopupManager = (() => {

    let dialogElement = null;
    let currentProductId = null;
    let cachedProductData = null;
    let cachedCouponTemplate = null;
    let cachedCouponTemplateDOM = null;
    let showProductDetailsPopupTimeout = null;
    let reinitProductPageTimeout = null;

    const getCouponTypeInfo = (couponType) => {
        switch(couponType) {
            case 0:
                return {
                    iconId: 'procent-regular',
                    name: __translations.discountPrice
                };
            case 1:
                return {
                    iconId: 'piggy-bank-regular', 
                    name: __translations.discountPrice
                };
            case 2:
                return {
                    iconId: 'truck-regular',
                    name: __translations.freeDelivery
                };
            case 3:
            case 4:
                return {
                    iconId: 'truck-regular',
                    name: __translations.deliveryDiscount
                };
            default:
                return {
                    iconId: 'procent-regular',
                    name: __translations.discountPrice
                };
        }
    };

    // Main popup display method
    const showCouponPopup = (config) => {
        const {
            productId,
            productUrl,
            phantomData = null,
            isProductDetails = false,
            triggerElement = null
        } = config;

        currentProductId = productId;
        
        if (isProductDetails) {
            showProductDetailsPopup(triggerElement);
        } else {
            showProductListPopup(productUrl, phantomData, triggerElement);
        }
    };

    // Product details popup handler
    const showProductDetailsPopup = (triggerElement) => {
        if (showProductDetailsPopupTimeout) {
            clearTimeout(showProductDetailsPopupTimeout);
        }
        
        showProductDetailsPopupTimeout = setTimeout(() => {
            cachedProductData = null;
            
            const dialog = getOrCreateDialog();
            if (!dialog) {
                return;
            }
            
            const product = getCurrentProductData(triggerElement);
            updateProductInfo(dialog, product);
            
            loadCouponData(dialog).then(() => {
                if (!document.contains(dialog)) {
                    document.body.appendChild(dialog);
                }
                openDialog(dialog);
            }).catch(() => {
                if (!document.contains(dialog)) {
                    document.body.appendChild(dialog);
                }
                openDialog(dialog);
            });
        }, 50);
    };

    const showProductListPopup = (_productUrl, phantomData, triggerElement) => {
        const dialog = getOrCreateDialog();
        const productData = phantomData || getCurrentProductData(triggerElement);

        if (productData) {
            updateProductInfo(dialog, productData);
            openDialog(dialog);
            loadCouponData(dialog);
        }
    };

    const getOrCreateDialog = () => {
        const existingDialogs = document.querySelectorAll('dialog.coupons-popup');
        existingDialogs.forEach(dialog => {
            if (dialog.open) {
                dialog.close();
            }
            dialog.remove();
        });
        
        dialogElement = null;
        
        const newDialog = createDialogElement();
        if (!newDialog) {
            return null;
        }
        
        document.body.appendChild(newDialog);
        dialogElement = newDialog;
        
        return newDialog;
    };

    const createDialogElement = () => {
        const template = document.getElementById('coupon-popup-template');
        if (template) {
            const clone = template.content.cloneNode(true);
            const dialog = clone.querySelector('dialog');
            attachDialogEvents(dialog);
            return dialog;
        } else {
            return null;
        }
    };

    const attachDialogEvents = (dialog) => {
        const closeButtons = dialog.querySelectorAll('.close-dialog-btn');
        closeButtons.forEach((button) => {
            button.addEventListener('click', () => {
                closeDialog();
            });
        });
        
        dialog.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                closeDialog();
            }
        });

        dialog.addEventListener('click', (e) => {
            if (e.target.classList.contains('learn-more-btn')) {
                closeDialog();
            }
        });
    };

    const loadCouponData = (dialog) => {
        if (!currentProductId) {
            return Promise.resolve();
        }

        
        const skeletonLoader = dialog.querySelector('.coupons-skeleton-loader-js');
        const couponsContainer = dialog.querySelector('.coupons-list-in-popup-js');
        if (skeletonLoader) {
            skeletonLoader.style.display = 'block';
        }
        if (couponsContainer) {
            couponsContainer.style.display = 'none';
        }

        return js.post({
            __action: 'Product/DiscountsGet',
            id: currentProductId
        }).then((response) => {
            const data = response?.action?.Object || {};
            const coupons = data.Coupons || [];
            
            if (data.Product) {
                updateProductInfo(dialog, data.Product);
            }
            
            return renderCoupons(dialog, coupons).then(() => {
                if (skeletonLoader) {
                    skeletonLoader.remove();
                }
                if (couponsContainer) {
                    couponsContainer.style.display = 'flex';
                }
                
                return coupons;
            });
        }).catch((error) => {
            console.error(__translations.errorLoadingCoupons, error);
            
            return renderCoupons(dialog, []).then(() => {
                if (skeletonLoader) {
                    skeletonLoader.remove();
                }
                if (couponsContainer) {
                    couponsContainer.style.display = 'flex';
                }
                
                throw error;
            });
        });
    };

    const renderCoupons = (dialog, coupons) => {
        const container = dialog.querySelector('.coupons-list-in-popup-js');
        
        if (coupons.length === 0) {
            container.innerHTML = '<div class="no-items">' + __translations.noAvailableCoupons + '</div>';
            return Promise.resolve();
        }
        
        return loadCouponTemplate().then((templateHTML) => {
            let template = cachedCouponTemplateDOM;
            
            if (!template) {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = templateHTML;
                template = tempDiv.querySelector('.coupon-item-js');
                
                if (template) {
                    cachedCouponTemplateDOM = template.cloneNode(true);
                }
            }
            
            if (!template) {
                return;
            }

            container.innerHTML = '';
            for (const coupon of coupons) {
                const couponElement = createPopupCouponFromTemplate(template, coupon);
                container.appendChild(couponElement);
            }
        }).catch((error) => {
            console.warn('Failed to render coupons:', error);
        });
    };

    const loadCouponTemplate = () => {
        if (cachedCouponTemplate) {
            return Promise.resolve(cachedCouponTemplate);
        }
        
        return js.get('template', 'partials/common/coupons-item.html').then((templateHTML) => {
            cachedCouponTemplate = templateHTML;
            return templateHTML;
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) {
            return null;
        }
        
        try {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return day + '.' + month + '.' + year + ' ' + __translations.timeSeparator + hours + ':' + minutes;
        } catch (error) {
            return dateString;
        }
    };

    const createPopupCouponFromTemplate = (template, coupon) => {
        const clone = template.cloneNode(true);
        const typeInfo = getCouponTypeInfo(coupon.Type);
        const validityText = formatCouponValidity(coupon.DateTo);

        updateCouponIcon(clone, typeInfo);
        updateCouponTypeName(clone, typeInfo);
        updateCouponCode(clone, coupon.Code);
        updateCopyButton(clone, coupon.Code);
        updateCouponValidity(clone, validityText);
        updateCouponDiscount(clone, coupon);
        updateLearnMoreButton(clone, coupon.Id);

        return clone;
    };

    const formatCouponValidity = (dateTo) => {
        if (dateTo) {
            return __translations.validityPrefix + formatDateTime(dateTo);
        }
        return '';
    };

    const updateCouponIcon = (clone, typeInfo) => {
        const iconElement = clone.querySelector('.coupon-icon-js');
        if (iconElement && typeInfo.iconId) {
            iconElement.innerHTML = '<svg class="coupon-icon-svg" aria-hidden="true"><use href="css/img/fontawesome-icons.svg?v=' + __lqTS + '#' + typeInfo.iconId + '"></use></svg>';
        }
    };

    const updateCouponTypeName = (clone, typeInfo) => {
        const typeNameElement = clone.querySelector('.coupon-type-name-js');
        if (typeNameElement) {
            typeNameElement.textContent = typeInfo.name;
        }
    };

    const updateCouponCode = (clone, code) => {
        const codeElement = clone.querySelector('.coupon-code-js');
        if (codeElement) {
            codeElement.textContent = code;
        }
    };

    const updateCopyButton = (clone, code) => {
        const copyButton = clone.querySelector('.copy-js');
        if (copyButton && code) {
            copyButton.setAttribute('data-text', code);
        }
    };

    const updateCouponValidity = (clone, validityText) => {
        const validityElement = clone.querySelector('.coupon-validity-js');
        if (validityElement) {
            validityElement.textContent = validityText;
        }
    };

    const updateCouponDiscount = (clone, coupon) => {
        const discountElement = clone.querySelector('.coupon-discount-js');
        if (!discountElement) {
            return;
        }

        let discountText;
        
        if (coupon.Type === 3 && coupon.Value) {
            discountText = '-' + coupon.Value.toPrice();
        } else if (coupon.Type === 4 && coupon.Value) {
            discountText = '-' + coupon.Value + '%';
        } else {
            discountText = '';
        }
        
        if (discountText) {
            discountElement.textContent = discountText;
            discountElement.style.display = 'block';
        }
    };

    const updateLearnMoreButton = (clone, couponId) => {
        const learnMoreBtn = clone.querySelector('.learn-more-btn');
        if (learnMoreBtn && couponId) {
            learnMoreBtn.dataset.couponId = couponId;
            const baseHref = learnMoreBtn.getAttribute('href');
            learnMoreBtn.setAttribute('href', baseHref + '&couponId=' + couponId);
        }
    };

    const _getProductNameFromDOM = () => {
        const nameElement = document.querySelector('.product-presentation h1, .product-name, h1, .single-product-name, .product-title');
        if (nameElement) {
            return nameElement.textContent.trim();
        }
        return '';
    };

    const _getProductCodeFromDOM = () => {
        const codeElements = document.querySelectorAll('.attribute__value, .product-code, .content-container__code, .product-code-js');
        for (const el of codeElements) {
            const text = el.textContent.replace(/[^:]*:\s*/, '').trim();
            if (text && text !== __translations.productCode && text.length > 2) {
                return text;
            }
        }
        return '';
    };

    const _getProductImageFromDOM = () => {
        const imageElement = document.querySelector('.single-image img:not(.no-image):not(.no-image-placeholder), .gallery-container img:not(.no-image):not(.no-image-placeholder), .image-container img:not(.no-image):not(.no-image-placeholder)');
        if (imageElement) {
            const imgSrc = imageElement.src || imageElement.getAttribute('data-src');
            if (imgSrc) {
                return { type: 'url', content: imgSrc };
            }
        }
        
        const svgElement = document.querySelector('.single-image svg.no-image, .gallery-container svg.no-image, .image-container svg.no-image');
        if (svgElement) {
            return { type: 'svg', content: svgElement.outerHTML };
        }
        
        return null;
    };

    const _getProductDataFromDOM = () => {
        if (!document.querySelector('.product-presentation')) {
            return { name: '', code: '', image: null };
        }

        return {
            name: _getProductNameFromDOM(),
            code: _getProductCodeFromDOM(),
            image: _getProductImageFromDOM()
        };
    };

    const _getProductDataFromHiddenInputs = (triggerElement) => {
        if (!triggerElement) {
            return { name: '', code: '', image: null };
        }

        const container = triggerElement.closest('.product-item-js, .cart-row, .product-item, .product-container, .coupons-on-product-presentation-js, .coupons-on-product-presentation, .product-item__coupons-container');
        
        if (!container) {
            return { name: '', code: '', image: null };
        }

        return extractProductDataFromContainer(container);
    };

    const extractProductDataFromContainer = (container) => {
        const result = { name: '', code: '', image: null };

        const hiddenNameInput = container.querySelector('input[name="productName"]');
        if (hiddenNameInput?.value) {
            result.name = hiddenNameInput.value;
        }

        const hiddenCodeInput = container.querySelector('input[name="productCode"]');
        if (hiddenCodeInput?.value) {
            result.code = hiddenCodeInput.value;
        }

        const hiddenImageInput = container.querySelector('input[name="productImage"]');
        if (hiddenImageInput?.value) {
            result.image = { type: 'url', content: hiddenImageInput.value };
        }

        return result;
    };

    const _imageToString = (imageObj) => {
        if (!imageObj) {
            return '';
        }
        
        if (imageObj.type === 'svg') {
            return 'SVG:' + imageObj.content;
        }
        
        return imageObj.content;
    };

    const getCurrentProductData = (triggerElement) => {
        const domData = _getProductDataFromDOM();
        const hiddenData = _getProductDataFromHiddenInputs(triggerElement);
        
        const productName = domData.name || hiddenData.name || __translations.productName;
        const productCode = domData.code || hiddenData.code || __translations.productCode;
        const productImage = _imageToString(domData.image || hiddenData.image);
        
        return {
            NameNoHtml: productName,
            Code: productCode,
            Image: { 
                ExternalUrl: productImage
            }
        };
    };

    const findNearestImagePlaceholder = () => {
        const combinedSelector = [
            '.product-item-js svg.no-image',
            '.product-container svg.no-image', 
            '.coupons-on-product-presentation-js svg.no-image',
            '.single-image svg.no-image',
            '.gallery-container svg.no-image',
            '.image-container svg.no-image',
            'svg.no-image',
            'img.no-image-placeholder',
            '.cart-row img.no-image-placeholder',
            '.product-item img.no-image-placeholder'
        ].join(', ');
        
        const element = document.querySelector(combinedSelector);
        if (!element) {
            return null;
        }
        
        if (element.tagName === 'SVG') {
            return 'SVG:' + element.outerHTML;
        } else {
            return 'PLACEHOLDER_IMG:' + element.outerHTML;
        }
    };

    const updateProductInfo = (dialog, product) => {
        const imgContainer = dialog.querySelector('.product__image-wrapper');
        const nameContainer = dialog.querySelector('.name-code-container__name');
        const codeContainer = dialog.querySelector('.name-code-container__code');

        if (!imgContainer || !nameContainer || !codeContainer) {
            return;
        }

        const productName = product?.NameNoHtml || product?.Name || __translations.productName;
        const productCode = product?.Code || __translations.productCode;
        const productImage = extractProductImage(product);

        updateProductImageContainer(imgContainer, productImage, productName);
        nameContainer.textContent = productName;
        codeContainer.textContent = productCode;
    };

    const extractProductImage = (product) => {
        if (!product) {
            return '';
        }

        if (typeof product.Image === 'string') {
            return product.Image;
        }
        
        if (product.Image?.ExternalUrl) {
            return product.Image.ExternalUrl;
        }
        
        if (product.ImageUrl) {
            return product.ImageUrl;
        }
        
        return '';
    };

    const updateProductImageContainer = (imgContainer, productImage, productName) => {
        if (productImage && productImage.trim() !== '') {
            renderProductImage(imgContainer, productImage, productName);
        } else {
            renderImagePlaceholder(imgContainer);
        }
    };

    const renderProductImage = (imgContainer, productImage, productName) => {
        if (productImage.startsWith('SVG:')) {
            const svgHTML = productImage.substring(4);
            imgContainer.innerHTML = '<div class="no-image-container">' + svgHTML + '</div>';
        } else if (productImage.startsWith('PLACEHOLDER_IMG:')) {
            const imgHTML = productImage.substring(16);
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = imgHTML;
            const img = tempDiv.querySelector('img');
            if (img) {
                img.classList.add('no-image-placeholder');
            }
            imgContainer.innerHTML = '<div class="no-image-container">' + tempDiv.innerHTML + '</div>';
        } else {
            imgContainer.innerHTML = '<div class="product-image-container"><img src="' + productImage + '" alt="' + productName + '"></div>';
        }
    };

    const renderImagePlaceholder = (imgContainer) => {
        const placeholder = findNearestImagePlaceholder();
        if (!placeholder) {
            return;
        }

        if (placeholder.startsWith('SVG:')) {
            const svgHTML = placeholder.substring(4);
            imgContainer.innerHTML = '<div class="no-image-container">' + svgHTML + '</div>';
        } else if (placeholder.startsWith('PLACEHOLDER_IMG:')) {
            const imgHTML = placeholder.substring(16);
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = imgHTML;
            const img = tempDiv.querySelector('img');
            if (img) {
                img.classList.add('no-image-placeholder');
            }
            imgContainer.innerHTML = '<div class="no-image-container">' + tempDiv.innerHTML + '</div>';
        } else {
            imgContainer.innerHTML = '<div class="no-image-container">' + placeholder + '</div>';
        }
    };

    const openDialog = (dialog) => {
        if (!dialog || typeof dialog.showModal !== 'function') {
            return;
        }
        
        if (!document.body.contains(dialog)) {
            document.body.appendChild(dialog);
        }
        
        dialogElement = dialog;
        
        try {
            dialog.showModal();
        } catch (error) {}
    };

    const closeDialog = () => {
        if (dialogElement?.close) {
            dialogElement.close();
        }
        
        const existingDialogs = document.querySelectorAll('dialog.coupons-popup');
        existingDialogs.forEach(dialog => dialog.remove());
        
        dialogElement = null;
    };

    const loadProductPageCoupons = () => {
        const couponsContainer = document.querySelector('.coupons-on-product-presentation-js');
        
        if (!couponsContainer) {
            return;
        }
        
        const productId = couponsContainer.dataset.id;
        if (!productId) {
            return;
        }

        js.post({
            __action: 'Product/DiscountsGet',
            id: productId
        }).then((response) => {
            const data = response?.action?.Object || {};
            const coupons = data.Coupons || [];
            
            if (coupons.length > 0) {
                const productInfo = getProductInfoForCoupons(couponsContainer, data.Product);
                cachedProductData = productInfo;
                renderProductPageCoupons(couponsContainer, coupons);
            }
        }).catch((error) => {
            console.error(__translations.errorLoadingCoupons, error);
        });
    };

    const getProductInfoForCoupons = (container, productFromAPI) => {
        const productNameInput = container.querySelector('input[name="productName"]');
        const productCodeInput = container.querySelector('input[name="productCode"]');
        const productImageInput = container.querySelector('input[name="productImage"]');
        
        const h1Element = document.querySelector('h1');
        const productName = productNameInput?.value || h1Element?.textContent.trim() || __translations.productName;
        const productCode = productCodeInput?.value || __translations.productCode;
        const productImage = productImageInput?.value || '';
        
        return productFromAPI || {
            NameNoHtml: productName,
            Code: productCode,
            Image: { ExternalUrl: productImage }
        };
    };

    const renderProductPageCoupons = (container, coupons) => {
        const maxVisible = 4;
        const visibleCoupons = coupons.slice(0, maxVisible);
        const hasMore = coupons.length > maxVisible;

        const skeletonLoader = container.querySelector('.product-coupons-skeleton-loader-js');
        const itemsContainer = container.querySelector('.coupons-items-js');
        const showMoreContainer = container.querySelector('.show-more-container-js');
        
        if (!itemsContainer) {
            skeletonLoader?.remove();
            return;
        }

        loadProductCouponsTemplate().then((templateHTML) => {
            processCouponsTemplate(templateHTML, itemsContainer, visibleCoupons, skeletonLoader);
            updateShowMoreContainer(showMoreContainer, hasMore, coupons.length);
        }).catch((error) => {
            console.error(__translations.errorLoadingCoupons, error);
            skeletonLoader?.remove();
        });
    };

    const processCouponsTemplate = (templateHTML, itemsContainer, visibleCoupons, skeletonLoader) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = templateHTML;
        const couponTemplate = tempDiv.querySelector('.coupon-template-js');
        
        if (!couponTemplate) {
            console.error(__translations.errorTemplateNotFound);
            skeletonLoader?.remove();
            return;
        }
        
        itemsContainer.innerHTML = '';
        
        visibleCoupons.forEach((coupon) => {
            const couponElement = createCouponFromTemplate(couponTemplate, coupon);
            if (couponElement) {
                itemsContainer.appendChild(couponElement);
            }
        });
        
        skeletonLoader?.remove();
        if (itemsContainer) {
            itemsContainer.style.display = 'flex';
        }
    };

    const updateShowMoreContainer = (showMoreContainer, hasMore, totalCount) => {
        if (hasMore && showMoreContainer) {
            const countElement = showMoreContainer.querySelector('.total-count-js');
            if (countElement) {
                countElement.textContent = totalCount;
            }
            showMoreContainer.style.display = 'block';
        }
    };

    const loadProductCouponsTemplate = () => {
        return js.get('template', 'partials/common/coupons-product-presentation.html');
    };

    const createCouponFromTemplate = (template, coupon) => {
        const clone = template.cloneNode(true);
        clone.style.display = 'flex';
        clone.classList.remove('coupon-template-js');
        
        const typeInfo = getCouponTypeInfo(coupon.Type);
        const name = coupon.NameEng || coupon.Name || typeInfo.name;

        const iconElement = clone.querySelector('.coupon-icon-js');
        if (iconElement && typeInfo.iconId) {
            iconElement.innerHTML = '<svg class="coupon-icon-svg" aria-hidden="true"><use href="css/img/fontawesome-icons.svg?v=' + __lqTS + '#' + typeInfo.iconId + '"></use></svg>';
        }

        const nameElement = clone.querySelector('.coupon-name-js');
        if (nameElement) {
            nameElement.textContent = name;
        }

        const discountElement = clone.querySelector('.coupon-discount-js');
        if (discountElement && coupon.DiscountPercent) {
            discountElement.textContent = '-' + coupon.DiscountPercent + '%';
            discountElement.style.display = 'block';
        }

        return clone;
    };

    const initProductPage = () => {
        if (document.querySelector('.coupons-on-product-presentation-js')) {
            loadProductPageCoupons();
        }
    };

    const clearProductDataCache = () => {
        cachedProductData = null;
    };

    const reinitProductPage = () => {
        if (reinitProductPageTimeout) {
            clearTimeout(reinitProductPageTimeout);
        }
        
        reinitProductPageTimeout = setTimeout(() => {
            clearProductDataCache();
            
            if (document.querySelector('.coupons-on-product-presentation-js')) {
                loadProductPageCoupons();
            }
        }, 0);
    };

    const initDOMObserver = () => {
        const observer = new MutationObserver((mutations) => {
            const shouldReinit = checkMutationsForReinit(mutations);
            
            if (shouldReinit) {
                reinitProductPage();
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
            attributeFilter: ['src', 'data-src']
        });

        return observer;
    };

    const checkMutationsForReinit = (mutations) => {
        for (const mutation of mutations) {
            if (shouldReinitForMutation(mutation)) {
                clearProductDataCache();
                return true;
            }
        }
        return false;
    };

    const shouldReinitForMutation = (mutation) => {
        if (mutation.type === 'characterData') {
            return true;
        }
        
        if (mutation.type === 'childList' && mutation.target.matches?.('h1, .product-name, .attribute__value, .product-code')) {
            return true;
        }
        
        if (mutation.type === 'attributes' && 
            mutation.target.matches?.('img') && 
            (mutation.attributeName === 'src' || mutation.attributeName === 'data-src')) {
            return true;
        }
        
        if (mutation.type === 'childList') {
            return checkAddedNodesForCoupons(mutation.addedNodes);
        }
        
        return false;
    };

    const checkAddedNodesForCoupons = (addedNodes) => {
        for (const node of addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
                if (node.classList?.contains('coupons-on-product-presentation-js')) {
                    return true;
                }
                if (node.querySelector?.('.coupons-on-product-presentation-js')) {
                    return true;
                }
            }
        }
        return false;
    };

    const setupEventListeners = () => {
        document.addEventListener('click', (e) => {
            if (e.target.closest('.parent-container-reload-js')) {
                clearProductDataCache();
                reinitProductPage();
            }
            
            const childReload = e.target.closest('.child-reload-js:not(.active-js):not(.prevent-instant-reload-js)');
            if (childReload) {
                clearProductDataCache();
                reinitProductPage();
            }
            
            if (e.target.closest('.last-level-attribute, .child-js, .attribute-option')) {
                clearProductDataCache();
            }
        });
    };

    const init = () => {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                initProductPage();
                initDOMObserver();
                setupEventListeners();
            });
        } else {
            initProductPage();
            initDOMObserver();
            setupEventListeners();
        }
    };

    return {
        showCouponPopup,
        initProductPage,
        initDOMObserver,
        clearProductDataCache,
        reinitProductPage,
        init
    };
})();

window.CouponPopupManager = CouponPopupManager;

CouponPopupManager.init();
`